import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {Alert, Box, Button, Grid2, LinearProgress} from '@mui/material';
import Typography from '@mui/material/Typography';
import { When } from 'react-if';
import { 
    GoogleMap, 
    MarkerF, 
    Circle
} from '@react-google-maps/api';
import { toast } from 'react-toastify';

import { RootState } from '../../store';
import { AlertTypes, DeviceTypes, EventTypes, ReportTypes } from '../../types';
import {
    EventMapButtonBar,
    defaultCoordinates,
} from './event-map-button-bar/eventMapButtonBar';
import { AlertDetailsDrawer } from '../details-drawers/alert-details-drawer';
import { DeviceDetailsDrawer } from '../details-drawers/device-details-drawer';
import Loading from '../loading';
import { markerConstants, markerRadiusColorConstants } from '../../constants/marker.constants';
import { EventFactory } from '../../factory/event.factory';
import { useGenericQuery } from '../../services/react-query/generic.query';
import { ReportDetailsDrawer } from '../details-drawers/report-details-drawer';
import { CustomMapPopup } from '../custom-map-popup';
import { MapLegend } from './map-legend';
import { UserMapLocationDetailsDrawer } from '../details-drawers/user-map-item-drawer';
import {SafetyReportDialog} from "../details-dialog/safety-report-dialog";
import {groupEvents,getMultipleEvents,getMarkerType,containsMultipleEvents} from "../../helpers/eventMapHelper";
import { useEffectiveSettings, SETTING } from "../../hooks/use-effective-settings";

// Number of consecutive failures before showing warning toast
const FAILURE_THRESHOLD = 3;
// Number of consecutive failures before giving up and showing persistent error
const MAX_FAILURES = 8;

export const ZOOM_LEVEL_EVENT = 18;

export interface LatLng {
    lat: number;
    lng: number;
}

interface Props {
    hideHeader?: boolean;
}

export const EventMap: React.FC<Props> = ({ hideHeader = false }) => {
    // Track consecutive failures for error handling
    const consecutiveFailures = useRef<number>(0);
    const hasShownErrorToast = useRef<boolean>(false);
    const [hasPersistentError, setHasPersistentError] = useState(false);
    
    // Track if map has ever been shown - once true, never goes back to false
    // This ensures the map is never unmounted after initial render
    const [hasMapMounted, setHasMapMounted] = useState(false);

    // Get activity filter and all toggle filters from store
    const { activityFilter, filter } = useSelector((state: RootState) => state.eventMap);
    const { getSetting, isLoading: settingIsLoading } = useEffectiveSettings();
    const activityStatusFiltersEnabled = getSetting(SETTING.activityStatusFilters, true);
    const mapSettingsDeviceEnabled = getSetting(SETTING.mapSettingsDeviceEnabled, false);

    // Build query URL with all filter params
    const buildQueryUrl = () => {
        const params = new URLSearchParams();
        
        // Activity filter (only if feature enabled)
        if (activityStatusFiltersEnabled) {
            params.append('activityFilter', activityFilter);
        }
        
        // Toggle filters - pass current state to backend
        params.append('includePomUsers', String(filter.devices));
        params.append('includeAlerts', String(filter.alerts));
        params.append('includeReports', String(filter.reports));
        params.append('includeSafetyReports', String(filter.safetyReport));
        
        return `eventMap/fetchDisplayEvents?${params.toString()}`;
    };
    
    const eventMapQueryUrl = buildQueryUrl();

    // Create unique query key that includes all filters to trigger refetch when any filter changes
    const queryKey = `fetchEventMapItems_${activityFilter}_${filter.devices}_${filter.alerts}_${filter.reports}_${filter.safetyReport}`;

    const { 
        data: unparsedEvents, 
        isLoading,
        isFetching,
        isError: isEventsError,
        errorUpdatedAt,  // Use this instead of dataUpdatedAt - changes on every error
        refetch: refetchEvents,
    } = useGenericQuery<EventTypes.EventMapsItems>(
        queryKey,
        eventMapQueryUrl,
        hasPersistentError ? undefined : 5000  // Stop polling if persistent error
    );
    const [events, setEvents] = useState<EventTypes.Model[]>([]);
    const [groupByEvents, setGroupByEvents] = useState<any>({});
    const mapRef = useRef<google.maps.Map | null>(null);

    const isMountedRef = useRef<boolean>(false);

    const [center, setCenter] = useState<LatLng>(
        defaultCoordinates.center
    );

    const [zoom, setZoom] = useState<number>(defaultCoordinates.zoom);
    const [selectedEvent, setSelectedEvent] = useState<EventTypes.Model | null>(null);
    const [eventModalVisible, setEventModalVisible] = useState(false);
    const [popoverPosition, setPopoverPosition] = useState<LatLng | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isMultipleEvents, setIsMultipleEvents] = useState(false);
    
    // Hover tracking for popup auto-close
    const [isHoveringPopup, setIsHoveringPopup] = useState(false);
    const popupCloseTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    // Delay before opening the popup on marker hover, so passing the cursor
    // across markers while panning/zooming doesn't flicker popups open.
    const hoverIntentTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const HOVER_INTENT_DELAY_MS = 150;

    let groupByKey : 'latLngString' | 'latLngStringApprox' = 'latLngStringApprox';
    if(zoom >= 16)
        groupByKey = 'latLngString';

    const handleMapLoad = (map: google.maps.Map) => {
        mapRef.current = map;
        map.controls[google.maps.ControlPosition.LEFT_TOP].push(
            document.getElementById('legend')!);
    };

    useEffect(() => {
        isMountedRef.current = true;
   
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    // Once initial loading is done (or we have data), mount the map and NEVER unmount it
    useEffect(() => {
        if (!hasMapMounted && (!isLoading || unparsedEvents)) {
            setHasMapMounted(true);
        }
    }, [isLoading, unparsedEvents, hasMapMounted]);

    // Auto-close popup when mouse leaves the popup card
    // Simple logic: popup stays open while hovering, closes after leaving with buffer
    useEffect(() => {
        // Clear any existing timeout when hovering state changes
        if (popupCloseTimeoutRef.current) {
            clearTimeout(popupCloseTimeoutRef.current);
            popupCloseTimeoutRef.current = null;
        }

        // If popup is visible but user is NOT hovering over it, start close timer
        if (!isHoveringPopup && popoverPosition !== null) {
            popupCloseTimeoutRef.current = setTimeout(() => {
                setPopoverPosition(null);
                setSelectedEvent(null);
                setIsHoveringPopup(false);
            }, 500); // 500ms buffer
        }

        return () => {
            if (popupCloseTimeoutRef.current) {
                clearTimeout(popupCloseTimeoutRef.current);
            }
        };
    }, [isHoveringPopup, popoverPosition]);

    // Handle error tracking for consecutive failures
    // Note: errorUpdatedAt changes on every error, ensuring this effect runs for each failed fetch
    useEffect(() => {
        if (isEventsError) {
            consecutiveFailures.current += 1;
            
            // Show toast after reaching failure threshold (only once)
            if (consecutiveFailures.current >= FAILURE_THRESHOLD && !hasShownErrorToast.current) {
                hasShownErrorToast.current = true;
                toast("Having trouble loading map data. We'll keep trying...", {
                    type: "warning",
                    autoClose: 5000,
                });
            }
            
            // After max failures, stop polling and show persistent error
            if (consecutiveFailures.current >= MAX_FAILURES && !hasPersistentError) {
                setHasPersistentError(true);
            }
        }
    }, [isEventsError, errorUpdatedAt, hasPersistentError]);

    // Reset failure count on successful data fetch
    useEffect(() => {
        if (unparsedEvents && !isEventsError) {
            // Data loaded successfully, reset failure tracking
            if (consecutiveFailures.current > 0 || hasPersistentError) {
                consecutiveFailures.current = 0;
                hasShownErrorToast.current = false;
                setHasPersistentError(false);
            }
        }
    }, [unparsedEvents, isEventsError, hasPersistentError]);
    
    // Handle retry after persistent error
    const handleRetry = () => {
        consecutiveFailures.current = 0;
        hasShownErrorToast.current = false;
        setHasPersistentError(false);
        refetchEvents();
    };

    useEffect(() => {
        if (unparsedEvents) {
            const evts = EventFactory.transformAlertsAndDevices(
                unparsedEvents.alerts, 
                mapSettingsDeviceEnabled ? (unparsedEvents.devices?.filter(d => d?.userId) as DeviceTypes.Model[]) : [],
                unparsedEvents.reports,
                // The "View POM User Location" setting (mapSettingsDeviceEnabled) governs BOTH
                // device AND mobile-app locations on the event map. When it's off, hide user
                // mobile locations too — otherwise they keep showing even though locations are
                // turned off in settings.
                mapSettingsDeviceEnabled ? (unparsedEvents.users ?? []) : [],
                filter,
            );

            setGroupByEvents(groupEvents(evts,groupByKey));
            setEvents(evts);
        }
    }, [unparsedEvents, filter, settingIsLoading, zoom]);

    useEffect(() => {
        if(containsMultipleEvents(groupByEvents))
            setIsMultipleEvents(true);
    }, [groupByEvents]);

    return (
        <>
            <When condition={hideHeader === false}>
                <Typography
                    fontSize={26}
                    gutterBottom={true}
                    color={'text.default'}
                    fontWeight={400}
                >
                    Event Map Monitoring
                </Typography>
            </When>

            {/* Persistent error alert after max failures */}
            {hasPersistentError && (
                <Alert 
                    severity="error" 
                    sx={{ mb: 2 }}
                    action={
                        <Button color="inherit" size="small" onClick={handleRetry}>
                            Retry
                        </Button>
                    }
                >
                    Unable to load map data. Please check your connection and try again.
                </Alert>
            )}

            <Grid2
                display={'flex'}
                direction={'row'}
                mb={2}
                sx={{ justifyContent: 'space-between' }}
            >
                <EventMapButtonBar setCenter={setCenter} setZoom={setZoom} />
            </Grid2>

            <Grid2 size={{md:12}} >
                <Box sx={{ height: '80vh', width: '100%', position: 'relative' }}>
                    {/* Show full loading spinner only before map has ever mounted */}
                    {!hasMapMounted && (
                        <Loading />
                    )}

                    {/* Once map has mounted, NEVER unmount it - keeps Google Maps stable */}
                    {hasMapMounted && (
                        <>
                            {/* Subtle loading indicator for background refetches */}
                            {isFetching && (
                                <LinearProgress 
                                    sx={{ 
                                        position: 'absolute', 
                                        top: 0, 
                                        left: 0, 
                                        right: 0, 
                                        zIndex: 1000 
                                    }} 
                                />
                            )}

                            <GoogleMap
                            options={{
                                disableDefaultUI: true,
                                zoomControl: true,
                                fullscreenControl: true,
                                mapTypeControl: true,
                                streetViewControl: true,
                                scaleControl: true,
                                rotateControl: true,
                                clickableIcons: false,
                                scrollwheel: true,
                                draggable: true,
                                draggableCursor: 'default',
                                draggingCursor: 'pointer',
                                keyboardShortcuts: true,
                                disableDoubleClickZoom: false,
                                noClear: false,
                                styles: [
                                    {
                                        featureType: 'poi',
                                        elementType: 'labels',
                                        stylers: [
                                            {
                                                visibility: 'off',
                                            },
                                        ],
                                    },
                                ],
                            }}
                            mapContainerStyle={{ height: '100%', width: '100%' }}
                            zoom={zoom}
                            center={center}
                            onLoad={(map) => handleMapLoad(map)}
                            onZoomChanged={() => {
                                const newZoomLevel = mapRef.current?.getZoom();
                                if (
                                    newZoomLevel &&
                                    newZoomLevel !== zoom &&
                                    isMountedRef.current
                                ) {
                                    setZoom(newZoomLevel);
                                }
                            }}
                            onDragStart={()=>{setIsDragging(true)}}
                            onDragEnd={()=>{
                                setIsDragging(false)
                            }}
                            onCenterChanged={() => {
                                if (!isDragging && mapRef?.current) {
                                    const newCenter = mapRef.current.getCenter()!;
                                    const newCenterObj = {
                                        lat: newCenter.lat(),
                                        lng: newCenter.lng(),
                                    };
                                    if (
                                        isMountedRef.current &&
                                        (newCenterObj.lat !== center.lat ||
                                            newCenterObj.lng !== center.lng)
                                    ) {
                                        setCenter(newCenterObj);
                                    }
                                }
                            }}
                        >
                            {events?.map((event, i) => (
                                <div
                                    key={`event_${event.content.id}`}
                                    id={`event_map_${event.content.id}`}
                                >
                                    <MarkerF
                                        key={event.content.id}
                                        clickable
                                        onMouseOver={(e) => {
                                            // Clear any pending close timer when hovering a new marker
                                            if (popupCloseTimeoutRef.current) {
                                                clearTimeout(popupCloseTimeoutRef.current);
                                                popupCloseTimeoutRef.current = null;
                                            }
                                            if (hoverIntentTimeoutRef.current) {
                                                clearTimeout(hoverIntentTimeoutRef.current);
                                            }
                                            hoverIntentTimeoutRef.current = setTimeout(() => {
                                                setIsHoveringPopup(true);
                                                setSelectedEvent(event);
                                                setPopoverPosition({
                                                    lat: Number(event?.latitude),
                                                    lng: Number(event?.longitude),
                                                });
                                            }, HOVER_INTENT_DELAY_MS);
                                        }}
                                        onMouseOut={() => {
                                            // Cancel a pending open if the cursor was just passing through
                                            if (hoverIntentTimeoutRef.current) {
                                                clearTimeout(hoverIntentTimeoutRef.current);
                                                hoverIntentTimeoutRef.current = null;
                                            }
                                            // When leaving marker, give user time to move to popup
                                            // The popup's onMouseEnter will cancel the timer if they make it
                                            setIsHoveringPopup(false);
                                        }}
                                        options={{
                                            icon: {
                                                url: markerConstants[getMarkerType(event,groupByKey,groupByEvents)!],
                                                scaledSize: new google.maps.Size(10, 10),
                                                scale: 1,
                                                origin: new google.maps.Point(0, 0),
                                            }
                                        }}
                                        onClick={(e) => {
                                            if (hoverIntentTimeoutRef.current) {
                                                clearTimeout(hoverIntentTimeoutRef.current);
                                                hoverIntentTimeoutRef.current = null;
                                            }
                                            setCenter({ lat: Number(event?.latitude), lng: Number(event?.longitude) });
                                            setSelectedEvent(event);
                                        }}
                                        position={{
                                            lat: Number(event?.latitude),
                                            lng: Number(event?.longitude),
                                        }}
                                    />

                                    {(!event?.isPrecise && zoom >= 15) &&
                                    <Circle
                                        center={{
                                            lat: Number(event?.latitude),
                                            lng: Number(event?.longitude),
                                        }}
                                        radius={event?.appxDistMeters}
                                        options={{
                                            strokeColor: markerRadiusColorConstants[getMarkerType(event,groupByKey,groupByEvents)!],
                                            strokeWeight: 0.1,
                                            fillOpacity: 0.2,
                                            fillColor: markerRadiusColorConstants[getMarkerType(event,groupByKey,groupByEvents)!],
                                            center: {
                                                lat: Number(event?.latitude),
                                                lng: Number(event?.longitude),
                                            }
                                        }}
                                    />
                                    }
                                </div>
                            ))}

                        </GoogleMap>

                            {/* Custom popup - positioned absolutely over the map */}
                            <CustomMapPopup
                                map={mapRef.current}
                                event={selectedEvent}
                                multipleEvents={selectedEvent ? getMultipleEvents(selectedEvent[groupByKey]!, groupByEvents) : null}
                                visible={selectedEvent !== null && popoverPosition !== null}
                                onClose={() => {
                                    if (popupCloseTimeoutRef.current) {
                                        clearTimeout(popupCloseTimeoutRef.current);
                                    }
                                    setPopoverPosition(null);
                                    setSelectedEvent(null);
                                    setIsHoveringPopup(false);
                                }}
                                onSeeMore={() => {
                                    if (popupCloseTimeoutRef.current) {
                                        clearTimeout(popupCloseTimeoutRef.current);
                                    }
                                    setPopoverPosition(null);
                                    setEventModalVisible(true);
                                    setIsHoveringPopup(false);
                                    if (selectedEvent?.latitude && selectedEvent?.longitude)
                                        setCenter({ lat: selectedEvent.latitude, lng: selectedEvent.longitude });
                                }}
                                setSelectedEvent={setSelectedEvent}
                                onMouseEnter={() => {
                                    // User entered popup - cancel any pending close timer
                                    if (popupCloseTimeoutRef.current) {
                                        clearTimeout(popupCloseTimeoutRef.current);
                                        popupCloseTimeoutRef.current = null;
                                    }
                                    setIsHoveringPopup(true);
                                }}
                                onMouseLeave={() => setIsHoveringPopup(false)}
                            />
                        </>
                    )}
                </Box>
            </Grid2>

            <When condition={selectedEvent?.type === 'alert'}>
                <AlertDetailsDrawer
                    onClose={() => {
                        setEventModalVisible(false);
                        setTimeout(() => {
                            setSelectedEvent(null);
                        }, 500);
                    }}
                    type={selectedEvent?.type}
                    visible={eventModalVisible}
                    alert={selectedEvent?.content as AlertTypes.Model}
                    zoomOut={() => setZoom(ZOOM_LEVEL_EVENT - 5)}
                />
            </When>

            <When condition={selectedEvent?.type === 'device' && eventModalVisible}>
                <DeviceDetailsDrawer
                    onClose={() => {
                        setEventModalVisible(false);
                        setTimeout(() => {
                            setSelectedEvent(null);
                        }, 500);
                    }}
                    visible={eventModalVisible}
                    device={selectedEvent?.content as DeviceTypes.Model}
                />
            </When>

            <When condition={selectedEvent?.type === 'userMobileLocation'}>
                <UserMapLocationDetailsDrawer
                    onClose={() => {
                        setEventModalVisible(false);
                        setTimeout(() => {
                            setSelectedEvent(null);
                        }, 500);
                    }}
                    visible={eventModalVisible}
                    event={selectedEvent!}
                />
            </When>

            <When condition={selectedEvent?.type === 'report' && eventModalVisible}>
                <ReportDetailsDrawer
                    onClose={() => {
                        setEventModalVisible(false);
                        setTimeout(() => {
                            setSelectedEvent(null);
                        }, 500);
                    }}
                    visible={eventModalVisible}
                    report={selectedEvent?.content as ReportTypes.Model}
                />
            </When>

            <When condition={selectedEvent?.type === 'safetyReport' && eventModalVisible}>
                <SafetyReportDialog report={selectedEvent?.content as ReportTypes.Model} visible={eventModalVisible} onClose={()=>{setEventModalVisible(false);
                    setTimeout(() => {
                    setSelectedEvent(null);
                }, 500);}}/>
            </When>

            <When condition={mapRef !== null}>
                <MapLegend isMultipleExists={containsMultipleEvents(groupByEvents)} isMultipleEvents={isMultipleEvents}/>
            </When>
        </>
    );
};
